<template>
  <div class="widget form-widget pb-2">
    <div class="widget-content">
      <div class="panel-body rounded-pills-icon tabs">
        <ul
          id="languageTab"
          class="nav nav-pills mb-4 mt-3 justify-content-center flex-md-row flex-column"
          role="tablist"
        >
          <li v-for="tab in tabs" :key="tab.code" class="nav-item ms-md-2 me-md-2 me-0 ms-0" role="presentation">
            <a
              :id="'tab-' + tab.code + '-tab'"
              :ref="'ref-' + tab.code"
              :aria-controls="'tab-' + tab.code"
              :aria-selected="currentTabCode && currentTabCode === tab.code"
              :class="
                'nav-link mb-2 text-center bgStyle' +
                (currentTabCode && currentTabCode === tab.code ? ' active' : '') +
                (tab.hidden ? ' d-none' : '')
              "
              :href="'#tab-' + tab.code"
              data-bs-toggle="pill"
              role="tab"
              @click="toggleTab(tab.code)"
            >
              <FontAwesomeIcon :icon="'fa-light fa-' + tab.icon" />
              {{ tab.title }}
            </a>
          </li>
        </ul>
        <div id="languageTabContent" class="tab-content">
          <div
            v-for="tab in tabs"
            :id="'tab-' + tab.code"
            :key="tab.code"
            :aria-labelledby="'tab-' + tab.code + '-tab'"
            :class="
              'tab-pane fade' +
              (currentTabCode && currentTabCode === tab.code ? ' show active' : '') +
              (tab.hidden ? ' d-none' : '')
            "
            role="tabpanel"
          >
            <FormComponent
              v-if="tab.code === templateConstante.TEMPLATE_DATA && tab.models"
              :models="tab.models"
              @formSubmit="submitForm"
              @formCancel="getRouterBackPath"
            />
            <TemplateVariableComponent v-if="tab.code === templateConstante.TEMPLATE_VARIABLES" :modules="tab.models" />
            <TemplateEditConditionsComponent
              v-if="tab.code === templateConstante.TEMPLATE_CONDITIONS"
              :models="tab.models"
              @formSubmit="updateConditions"
              @formCancel="getRouterBackPath"
            />
          </div>
        </div>
      </div>
      <BlockLoader :loading="loading" />
    </div>
  </div>
</template>

<script>
import { apiConnection } from "@/services/api-connection";
import { formManager } from "@/services/form/form-manager";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import FormComponent from "@/components/FormComponent.vue";
import { notificationManager } from "@/services/utilities/notification-manager";
import { templateConstante } from "@/constantes/template";
import { templateManager } from "@/services/template/template-manager";
import { i18n } from "@/i18n";
import TemplateEditConditionsComponent from "@/components/template/TemplateEditConditionsComponent.vue";
import TemplateVariableComponent from "@/components/template/TemplateVariableComponent.vue";

export default {
  name: "TemplateEditView",
  computed: {
    templateConstante() {
      return templateConstante;
    },
  },
  components: {
    TemplateVariableComponent,
    TemplateEditConditionsComponent,
    FormComponent,
    BlockLoader,
  },
  data() {
    return {
      loading: false,
      FormTitle: "",
      templateType: "",
      tabs: [],
      currentTabCode: null,
      editItem: [],
    };
  },
  watch: {
    tabs() {
      this.checkHash();
    },
  },
  mounted() {
    this.templateType = this.$route.params.templateType;
    this.init();
  },
  methods: {
    init() {
      this.FormTitle = this.$tc("global.view.title", 1, {
        name: this.$tc("admin.titleRncp.title", 1).toLowerCase(),
      });
      this.getTemplateToEdit();
    },
    getRouterBackPath() {
      this.$router.push({
        name: this.$route.meta.backPath,
        params: { templateType: this.templateType },
      });
    },
    checkHash() {
      if (!window.location.hash && this.tabs && this.tabs[0]) {
        this.setCurrentTabCode(this.tabs[0].code);
      } else {
        this.setCurrentTabCode(window.location.hash.substring(1));
      }
    },
    setCurrentTabCode(code) {
      this.currentTabCode = code;
      window.location.hash = this.currentTabCode;
    },
    toggleTab(code) {
      this.setCurrentTabCode(code);
    },
    async getTemplateToEdit() {
      this.loading = true;
      const response = await templateManager.getTemplateToEdit(this.templateType, this.$route.params.id);

      this.tabs = response.tabs;
      this.checkHash();
      this.loading = false;
    },
    submitForm(validForm) {
      const baseUrl = `/app/admin/template/${this.templateType}/update/` + this.$route.params.id;
      const response = formManager.processForm(validForm, false);
      this.loading = true;
      apiConnection.put(baseUrl, response).then((data) => {
        formManager.processResponse(data, null, i18n.global.t("admin.template.success"), validForm);
        this.loading = false;
        this.getRouterBackPath();
      });
    },
    copyCode(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          notificationManager.showNotification(
            "success",
            this.$tc("admin.copy.success", 2, {
              name: this.$t("admin.variable.title"),
            }),
          );
        })
        .catch(() => {
          notificationManager.showNotification(
            "error",
            this.$tc("admin.copy.error", 2, {
              name: this.$t("admin.variable.title"),
            }),
          );
        });
    },
    updateConditions(response) {
      this.loading = true;
      templateManager.updateConditions(this.templateType, this.$route.params.id, response).then((data) => {
        formManager.processResponse(
          data,
          null,
          this.$tc("global.edit.success", 1, {
            name: this.$tc("admin.template.title", 1),
          }),
          response,
        );
        this.loading = false;
        if (data?.responses[0]?.status === "success") {
          this.$router.push({
            name: this.$route.meta.backPath,
            params: { entity: this.alias },
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.widget {
  transition: all 300ms ease-in-out;

  &.padding {
    padding: 20px;
  }
}

.content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  @include up-to-lg {
    flex-direction: column;
  }

  .left {
    width: 100%;
    transition: all 300ms ease-out;
  }

  .right {
    width: 0;
    transition: all 300ms ease-out;
    overflow: hidden;
  }

  &.editing {
    .left {
      width: 66%;

      @include up-to-lg {
        width: 100%;
      }
    }

    .right {
      margin-left: 20px;
      width: 33%;
      @include up-to-lg {
        width: 100%;
        margin-left: 0;
      }
    }
  }
}

.item {
  padding: 5px 25px;
  border: 1px solid $grey;
  border-radius: $radius;
  min-height: 50px;
  align-items: center;
  margin-bottom: 10px;

  @include dark-theme {
    background: $m-color_9;
  }

  &.editing {
    cursor: initial;
  }

  &.active {
    background: $light-blue;

    @include dark-theme {
      background: $dark-grey;
    }
  }

  @include up-to-md {
    padding: 15px;
  }

  &__heading {
    display: flex;
    align-items: center;
  }

  &__title {
    font-size: 16px;
    color: $admin-color;

    @include mobile {
      max-width: 195px;
      overflow: hidden;
      -ms-text-overflow: ellipsis;
      text-overflow: ellipsis;
    }

    @include dark-theme {
      color: $white;
    }
  }

  &__icon {
    margin-right: 15px;
    font-size: 22px;

    @include up-to-md {
      margin-right: 10px;
      font-size: 18px;
    }
  }

  &__editIcon {
    width: 15px;
    height: 15px;
  }
}

.parent.ex-1 .drag-drop {
  @include up-to-md {
    padding: 0;
  }
}

.widget-content {
  min-height: 200px;
  position: relative;
}
</style>
